/**
 * Aggregation primitives + the three pitch aggregators that compose them.
 *
 * Callers:
 *   - queries.js — per-athlete stats for the home roster + profile pages.
 *   - ta-sessions-table.js / ta-sessions-chart.js — per-session
 *     aggregations for the sessions table and across-sessions chart.
 *
 * Null-handling rule (consistent across every reduction here):
 *   - Nulls are excluded from avg / peak.
 *   - Empty or all-null input → both metrics are null (not 0).
 *   - Callers render null as the em-dash placeholder ("—").
 */

// ============================================================
// Cross-source merger — for the unified throws timeline view.
// ============================================================
/**
 * Merge Rapsodo pitches + PR throws into a unified row format. The
 * tool-agnostic view treats both as a single stream — source is kept
 * as metadata for tooltips but not surfaced visually. Sorted by
 * thrown_at ascending so chart code can iterate without re-sorting.
 *
 * Output row shape:
 *   {
 *     thrown_at,
 *     category,        // pitch_type for Rapsodo, throw_context for PR
 *     velo_mph,        // unified velocity field
 *     total_spin_rpm, true_spin_rpm, spin_eff_pct,    // Rapsodo only
 *     ind_vert_break_in, horz_break_in,               // Rapsodo only
 *     rel_height_ft, rel_side_ft,                     // Rapsodo only
 *     source,          // 'rapsodo' | 'pocket_radar'
 *     id,
 *   }
 *
 * For PR rows, the Rapsodo-only fields are undefined. The chart filters
 * by metric != null, so PR throws naturally drop out when a spin/break
 * metric is selected — leaving just the Rapsodo subset.
 */
export function mergeThrows({ pitches = [], throws = [] } = {}) {
  const out = [];
  for (const p of pitches) {
    out.push({
      thrown_at: p.thrown_at,
      session_date: utcToChicagoDate(p.thrown_at),
      category: p.pitch_type ?? 'OTH',
      velo_mph: p.rel_speed_mph,
      total_spin_rpm: p.total_spin_rpm,
      true_spin_rpm: p.true_spin_rpm,
      spin_eff_pct: p.spin_eff_pct,
      ind_vert_break_in: p.ind_vert_break_in,
      horz_break_in: p.horz_break_in,
      rel_height_ft: p.rel_height_ft,
      rel_side_ft: p.rel_side_ft,
      source: 'rapsodo',
      id: p.id,
    });
  }
  for (const t of throws) {
    out.push({
      thrown_at: t.thrown_at,
      session_date: utcToChicagoDate(t.thrown_at),
      category: t.throw_context ?? 'other',
      velo_mph: t.velo_mph,
      source: 'pocket_radar',
      id: t.id,
    });
  }
  out.sort((a, b) => a.thrown_at.localeCompare(b.thrown_at));
  return out;
}

// Convert a UTC ISO timestamp to a YYYY-MM-DD date string in
// America/Chicago — the canonical "session day" for TAP's facility.
// Used by mergeThrows to bucket throws into session-day columns for
// the unified timeline chart.
function utcToChicagoDate(isoUtc) {
  if (!isoUtc) return null;
  return new Date(isoUtc).toLocaleDateString('en-CA', {
    timeZone: 'America/Chicago',
  });
}

// ============================================================
// Primitives
// ============================================================

/** Group rows by a key. Rows with a null/undefined key are skipped. */
export function groupBy(rows, key) {
  const out = {};
  for (const r of rows ?? []) {
    const k = r[key];
    if (k == null) continue;
    if (!out[k]) out[k] = [];
    out[k].push(r);
  }
  return out;
}

/** Count rows by a key. Rows with a null/undefined key are skipped. */
export function countBy(rows, key) {
  const out = {};
  for (const r of rows ?? []) {
    const k = r[key];
    if (k == null) continue;
    out[k] = (out[k] ?? 0) + 1;
  }
  return out;
}

/** Most-frequent key in a counts map. Returns null on empty input. */
export function topKey(counts) {
  let best = null;
  let bestN = -1;
  for (const [k, n] of Object.entries(counts)) {
    if (n > bestN) { best = k; bestN = n; }
  }
  return best;
}

/** Arithmetic mean. Returns null for empty input. */
export function mean(arr) {
  if (!arr.length) return null;
  let sum = 0;
  for (const v of arr) sum += v;
  return sum / arr.length;
}

/**
 * For every metric field, compute { avg_<field>, peak_<field> } over rows.
 * Nulls are excluded; all-null or empty rows → both metrics null.
 */
export function reduceMetrics(rows, fields) {
  const out = {};
  for (const field of fields) {
    const values = (rows ?? []).map((r) => r[field]).filter((v) => v != null);
    out[`avg_${field}`] = values.length ? mean(values) : null;
    out[`peak_${field}`] = values.length ? Math.max(...values) : null;
  }
  return out;
}

// ============================================================
// Constants
// ============================================================

// Every Rapsodo metric we aggregate per session. Mirrors ta-trend.js's
// METRIC_LABELS so the across-sessions chart offers the same metrics as
// the per-pitch detail chart, just averaged.
export const RAPSODO_METRIC_FIELDS = [
  'rel_speed_mph',
  'total_spin_rpm',
  'true_spin_rpm',
  'spin_eff_pct',
  'ind_vert_break_in',
  'horz_break_in',
  'rel_height_ft',
  'rel_side_ft',
  'vaa',
  'haa',
];

// ============================================================
// Aggregators
// ============================================================

/**
 * Per-athlete stats from a flat pitches array. Used by the home roster
 * and athlete profile for a glanceable summary.
 *
 * Output (keyed by athlete_id):
 *   { count, peakVelo, lastThrownAt, primaryPitchType, intentSummary,
 *     pitchTypes, intents }
 */
export function aggregatePitchStatsByAthlete(pitches) {
  const byAthlete = {};
  for (const p of pitches ?? []) {
    const s = byAthlete[p.athlete_id] ??= {
      count: 0,
      peakVelo: null,
      lastThrownAt: '',
      pitchTypes: {},
      intents: {},
    };
    s.count++;
    if (p.rel_speed_mph != null && (s.peakVelo == null || p.rel_speed_mph > s.peakVelo)) {
      s.peakVelo = p.rel_speed_mph;
    }
    if ((p.thrown_at ?? '') > s.lastThrownAt) s.lastThrownAt = p.thrown_at;
    if (p.pitch_type) s.pitchTypes[p.pitch_type] = (s.pitchTypes[p.pitch_type] ?? 0) + 1;
    if (p.intent_type) s.intents[p.intent_type] = (s.intents[p.intent_type] ?? 0) + 1;
  }
  for (const id of Object.keys(byAthlete)) {
    const s = byAthlete[id];
    s.primaryPitchType = topKey(s.pitchTypes);
    s.intentSummary = summarizeIntent(s.intents);
  }
  return byAthlete;
}

/**
 * Per-session Rapsodo aggregations: every metric in RAPSODO_METRIC_FIELDS
 * gets avg + peak, plus strike_pct + primary_pitch_type + pitch_type_counts.
 *
 * Friendly aliases avg_velo / peak_velo are kept for the table columns
 * and chart defaults that don't want to know about rel_speed_mph.
 */
export function aggregateRapsodoSessions(sessions, pitches) {
  const byUpload = groupBy(pitches, 'upload_id');
  return (sessions ?? []).map((s) => {
    const rows = byUpload[s.id] ?? [];
    const strikes = rows.filter((r) => r.is_strike === true).length;
    const ballsAndStrikes = rows.filter((r) => r.is_strike === true || r.is_strike === false).length;
    const types = countBy(rows, 'pitch_type');

    const out = {
      ...s,
      ...reduceMetrics(rows, RAPSODO_METRIC_FIELDS),
      strike_pct: ballsAndStrikes > 0 ? (strikes / ballsAndStrikes) * 100 : null,
      primary_pitch_type: topKey(types),
      pitch_type_counts: types,
    };

    out.avg_velo = out.avg_rel_speed_mph;
    out.peak_velo = out.peak_rel_speed_mph;
    return out;
  });
}

/**
 * Per-session Pocket Radar aggregations. Velocity-only (PR has no other
 * metrics) plus context counts.
 */
export function aggregatePocketRadarSessions(sessions, throws) {
  const byUpload = groupBy(throws, 'upload_id');
  return (sessions ?? []).map((s) => {
    const rows = byUpload[s.id] ?? [];
    const velos = rows.map((r) => r.velo_mph).filter((v) => v != null);
    const contexts = countBy(rows, 'throw_context');
    return {
      ...s,
      avg_velo: velos.length ? mean(velos) : null,
      peak_velo: velos.length ? Math.max(...velos) : null,
      primary_context: topKey(contexts),
      context_counts: contexts,
    };
  });
}

// ============================================================
// Private helpers
// ============================================================

function summarizeIntent(intents) {
  const keys = Object.keys(intents);
  if (keys.length === 0) return null;
  if (keys.length === 1) return keys[0];
  return 'mixed';
}
